"use client";

import React from "react";
import { PhoneCall, Clock, ShieldCheck, Wrench, MapPin, CircleCheck as CheckCircle2, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
export default function Riverdale() {
  const shouldReduceMotion = useReducedMotion();
  const trustHighlights = [{
    text: "24/7 Immediate Dispatch in Riverdale"
  }, {
    text: "Licensed, Insured & Local Technicians"
  }, {
    text: "Upfront Pricing With No Hidden Fees"
  }];
  const quickStats = [{
    label: "Emergency Response",
    value: "30-60 Min"
  }, {
    label: "Availability",
    value: "24/7/365"
  }, {
    label: "Riverdale Rating",
    value: "5.0 Stars"
  }];
  return <section id="riverdalehero" data-nav="dark" className="relative overflow-hidden bg-primary text-primary-foreground py-16 md:py-24 lg:py-28">
      {/* Background Gold Accent Tints and Glows */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full bg-accent/15 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-accent/10 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,var(--color-accent)/0.08,transparent_60%)]" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          
          {/* Left Column: Core Messaging & CTAs */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: "easeOut"
        }} className="flex flex-col items-start lg:col-span-7">
            {/* Location & Status Badge */}
            <div className="mb-6 flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="bg-accent text-accent-foreground font-semibold px-3 py-1 text-xs sm:text-sm border-none shadow-sm">
                <MapPin className="size-3.5 mr-1" />
                Riverdale, GA
              </Badge>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-foreground/10 px-3 py-1 text-xs font-medium text-primary-foreground backdrop-blur-sm border border-primary-foreground/15">
                <Clock className="size-3 text-accent" />
                24/7 Emergency Service
              </span>
            </div>

            {/* Main Headline */}
            <h1 className="font-default text-3xl font-bold tracking-tight text-primary-foreground sm:text-4xl md:text-5xl lg:text-5xl/tight">
              Plumbing Services in Riverdale, GA
            </h1>

            {/* Subheading */}
            <p className="mt-4 text-lg sm:text-xl font-normal text-primary-foreground/90 max-w-2xl">
              Expert Plumbing Help Available 24/7 in Riverdale
            </p>

            <p className="mt-3 text-sm sm:text-base text-primary-foreground/75 max-w-xl leading-relaxed">
              Fast, dependable plumbing repairs and emergency solutions for homes and businesses across Riverdale and surrounding Clayton County neighborhoods.
            </p>

            {/* Feature Bullet Points */}
            <ul className="mt-6 space-y-2.5 text-sm sm:text-base text-primary-foreground/90">
              {trustHighlights.map((item, index) => <li key={index} data-index={index} className="flex items-center gap-2.5">
                  <CheckCircle2 className="size-5 shrink-0 text-accent" />
                  <span>{item.text}</span>
                </li>)}
            </ul>

            {/* Call to Action Buttons */}
            <div className="mt-8 flex w-full flex-col gap-3.5 sm:w-auto sm:flex-row sm:items-center">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold shadow-md px-6 text-base h-12">
                <Link to="/contact/">
                  <Wrench className="size-4 mr-1.5" />
                  Request Service
                </Link>
              </Button>

              <Button asChild size="lg" variant="outline" className="bg-transparent border-accent text-accent hover:bg-accent hover:text-accent-foreground font-semibold shadow-sm px-6 text-base h-12 transition-colors">
                <a href="tel:4708479076">
                  <PhoneCall className="size-4 mr-1.5" />
                  Call (470) 847-9076
                </a>
              </Button>
            </div>

            {/* Quick Metrics */}
            <div className="mt-10 grid w-full grid-cols-3 gap-3 border-t border-primary-foreground/15 pt-6 sm:gap-6">
              {quickStats.map((stat, index) => <div key={index} data-index={index} className="flex flex-col">
                  <span className="font-default text-lg sm:text-xl font-bold text-accent">
                    {stat.value}
                  </span>
                  <span className="text-xs sm:text-sm text-primary-foreground/75">
                    {stat.label}
                  </span>
                </div>)}
            </div>
          </motion.div>

          {/* Right Column: Hero Visual & Service Card */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          scale: 1
        } : {
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.15,
          ease: "easeOut"
        }} className="relative lg:col-span-5">
            {/* Decorative Card Container with Gold Border Accent */}
            <div className="relative mx-auto max-w-md lg:max-w-none rounded-2xl border border-accent/30 bg-card p-3 sm:p-4 shadow-xl text-card-foreground">
              
              <div className="relative overflow-hidden rounded-xl bg-muted aspect-4/3 sm:aspect-16/11">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/riverdale_plumbing_hero.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/riverdale_plumbing_hero.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/riverdale_plumbing_hero.png-wvc-sizes" alt="Professional Riverdale plumber repairing home water pipes" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-transparent" />
                
                {/* On-image badge */}
                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between gap-2 rounded-lg bg-primary/90 p-3 text-primary-foreground backdrop-blur-sm border border-accent/30">
                  <div className="flex items-center gap-2.5">
                    <div className="flex size-9 items-center justify-center rounded-full bg-accent text-accent-foreground font-bold">
                      <ShieldCheck className="size-5" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-accent">Clayton County Certified</p>
                      <p className="text-xs text-primary-foreground/90">Rapid Riverdale Response</p>
                    </div>
                  </div>
                  <span className="hidden sm:inline-flex items-center text-xs font-medium text-accent">
                    <Sparkles className="size-3.5 mr-1" />
                    Guaranteed
                  </span>
                </div>
              </div>

              {/* Service Capabilities Mini-Bar */}
              <div className="mt-4 grid grid-cols-2 gap-2 text-xs sm:text-sm">
                <div className="flex items-center gap-2 rounded-lg bg-muted/70 p-2.5">
                  <Wrench className="size-4 text-accent shrink-0" />
                  <span className="font-medium">Pipe & Leak Repairs</span>
                </div>
                <div className="flex items-center gap-2 rounded-lg bg-muted/70 p-2.5">
                  <Clock className="size-4 text-accent shrink-0" />
                  <span className="font-medium">Water Heater Service</span>
                </div>
              </div>
            </div>

            {/* Floating Gold Dispatch Tag */}
            <div className="absolute -bottom-4 -left-3 sm:-left-6 rounded-xl border border-accent/40 bg-accent px-4 py-2.5 text-accent-foreground shadow-lg flex items-center gap-2 font-semibold text-xs sm:text-sm">
              <PhoneCall className="size-4" />
              <span>Direct Line: (470) 847-9076</span>
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}